<template>
  <v-dialog
    v-model="dialog"
    persistent
    :max-width="$vuetify.breakpoint.mobile ? '98vw' : '1100'"
    content-class="manage-payment-dialog"
  >
    <v-card style="position: relative;" class="mp-card-root">
      <v-overlay :value="busy" absolute opacity="0.55" z-index="6">
        <div class="d-flex flex-column align-center white--text">
          <v-progress-circular indeterminate color="white" size="48" class="mb-3"></v-progress-circular>
          <div class="text-center">{{ busyMessage }}</div>
        </div>
      </v-overlay>

      <div class="mp-header">
        <v-avatar color="rgba(255,255,255,0.15)" size="44" class="mp-header-icon">
          <v-icon :style="{ color: 'white !important' }">mdi-credit-card-outline</v-icon>
        </v-avatar>
        <div class="mp-header-text">
          <div class="mp-header-title">{{ $t('dueDiligence.managePaymentTitle') }}</div>
          <div class="mp-header-subtitle">{{ $t('dueDiligence.managePaymentSubtitle') }}</div>
        </div>
        <v-spacer></v-spacer>
        <div class="mp-header-actions">
          <v-btn
            outlined
            small
            color="white"
            class="mp-header-btn"
            :class="{ 'mp-header-btn--active': step === 1 }"
            :disabled="busy"
            @click="viewInvoice"
          >
            <v-icon :left="!$vuetify.breakpoint.mobile" small :style="{ color: 'white !important' }">mdi-file-document-outline</v-icon>
            <span v-if="!$vuetify.breakpoint.mobile">{{ $t('dueDiligence.viewInvoice') }}</span>
          </v-btn>
          <v-btn
            outlined
            small
            color="white"
            class="mp-header-btn"
            :loading="downloading"
            :disabled="busy"
            @click="downloadPdf"
          >
            <v-icon :left="!$vuetify.breakpoint.mobile" small :style="{ color: 'white !important' }">mdi-download</v-icon>
            <span v-if="!$vuetify.breakpoint.mobile">{{ $t('dueDiligence.downloadInvoice') }}</span>
          </v-btn>
          <v-btn
            v-if="canRelease"
            depressed
            small
            class="mp-header-btn mp-header-btn--danger"
            :disabled="busy"
            @click="$emit('release')"
          >
            <v-icon :left="!$vuetify.breakpoint.mobile" small :style="{ color: 'white !important' }">mdi-account-remove-outline</v-icon>
            <span v-if="!$vuetify.breakpoint.mobile">{{ $t('dueDiligence.exitAsPayer') }}</span>
          </v-btn>
        </div>
        <v-btn icon :disabled="busy" @click="closeModal">
          <v-icon :style="{ color: 'white !important' }">mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="mp-summary-bar">
        <div class="mp-summary-item">
          <span class="mp-summary-label">{{ $t('dueDiligence.ddsReferenceNo') }}</span>
          <span class="mp-summary-value">{{ ddsReference }}</span>
        </div>
        <div class="mp-summary-item" v-if="country">
          <span class="mp-summary-label">{{ $t('deforestation.country') }}</span>
          <span class="mp-summary-value">{{ country }}</span>
        </div>
        <div class="mp-summary-item">
          <span class="mp-summary-label">{{ $t('deforestation.product') }}</span>
          <span class="mp-summary-value">{{ productName }}</span>
        </div>
        <div class="mp-summary-item">
          <span class="mp-summary-label">{{ $t('dueDiligence.quantityNetMass') }}</span>
          <span class="mp-summary-value">{{ formattedNetMass }} {{ $t('dueDiligence.unitTon') }}</span>
        </div>
        <div class="mp-amount-chip">
          <span class="mp-summary-label">{{ $t('dueDiligence.totalAmountPayable') }}</span>
          <span class="mp-amount-value">{{ formattedAmount }}</span>
          <span class="mp-summary-label">{{ $t('dueDiligence.paymentTypeDdsReviewFee') }}</span>
        </div>
      </div>

      <!-- Stepper header -->
      <div class="mp-stepper">
        <div v-for="(s, idx) in steps" :key="idx" class="mp-step-wrap">
          <div class="mp-step" :class="{ 'mp-step--clickable': isStepNavigable(idx + 1) }" @click="goToStep(idx + 1)">
            <div class="mp-step-circle" :class="'mp-step-circle--' + stepStatus(idx + 1)">
              <v-icon v-if="stepStatus(idx + 1) === 'done'" small :style="{ color: 'white !important' }">mdi-check</v-icon>
              <span v-else>{{ idx + 1 }}</span>
            </div>
            <div class="mp-step-text">
              <div class="mp-step-title" :class="'mp-step-title--' + stepStatus(idx + 1)">{{ s.title }}</div>
              <div class="mp-step-desc">{{ s.desc }}</div>
            </div>
          </div>
          <div
            v-if="idx < steps.length - 1"
            class="mp-step-line"
            :class="{ 'mp-step-line--done': stepStatus(idx + 1) === 'done' }"
          ></div>
        </div>
      </div>

      <v-card-text class="pa-0 mp-body-scroll">
        <div class="mp-body">
          <div class="mp-main">

            <!-- STEP 1: Review Details (v-show so download/print can capture the invoice from any step) -->
            <div v-show="step === 1 || capturingInvoice" :class="{ 'invoice-capture-offscreen': capturingInvoice && step !== 1 }">
              <div class="mp-card">
                <div class="mp-card-header">
                  <v-icon color="primary">mdi-file-document-outline</v-icon>
                  <div>
                    <div class="mp-card-title">1. {{ $t('dueDiligence.invoiceHeader') }}</div>
                    <div class="mp-card-subtitle">{{ $t('dueDiligence.invoiceDetailsHint') }}</div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-btn text small color="primary" @click="printInvoice">
                    <v-icon left small>mdi-printer</v-icon>{{ $t('deforestation.print') }}
                  </v-btn>
                </div>
                <div class="mp-card-body pa-0">
                  <div ref="invoiceDocument" id="dds-invoice-document" class="invoice-letterhead">
                    <div class="invoice-letterhead__top">
                      <img src="/img/id/idconnect.png" alt="ID Connect" class="invoice-letterhead__logo" />
                      <div class="invoice-letterhead__badge">{{ $t('dueDiligence.ddsReviewFeeInvoice') }}</div>
                    </div>
                    <div class="invoice-letterhead__meta">
                      <div class="mp-field">
                        <span class="label">{{ $t('dueDiligence.invoiceNumber') }}</span>
                        <span class="value">{{ invoiceNumber }}</span>
                      </div>
                      <div class="mp-field">
                        <span class="label">{{ $t('dueDiligence.invoiceDate') }}</span>
                        <span class="value">{{ invoiceDate }}</span>
                      </div>
                    </div>

                    <div class="invoice-section">
                      <div class="invoice-section__title">{{ $t('dueDiligence.ddsProductInformation') }}</div>
                      <div class="mp-grid-2">
                        <div class="mp-field">
                          <span class="label">{{ $t('dueDiligence.ddsReferenceNo') }}</span>
                          <span class="value">{{ ddsReference }}</span>
                        </div>
                        <div class="mp-field">
                          <span class="label">{{ $t('deforestation.product') }}</span>
                          <span class="value">{{ productName }}</span>
                        </div>
                        <div class="mp-field">
                          <span class="label">{{ $t('dueDiligence.quantityNetMass') }}</span>
                          <span class="value">{{ formattedNetMass }} {{ $t('dueDiligence.unitTon') }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="invoice-section">
                      <div class="invoice-section__title">{{ $t('dueDiligence.payerInformation') }}</div>
                      <div class="mp-grid-2">
                        <div class="mp-field">
                          <span class="label">{{ $t('dueDiligence.exporterName') }}</span>
                          <span class="value">{{ payer.name }}</span>
                        </div>
                        <div class="mp-field">
                          <span class="label">{{ $t('dueDiligence.exporterContact') }}</span>
                          <span class="value">{{ payer.contact }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="invoice-section invoice-section--highlight">
                      <div class="invoice-section__title">{{ $t('dueDiligence.pricingInformation') }}</div>
                      <div class="mp-grid-2">
                        <div class="mp-field">
                          <span class="label">{{ $t('dueDiligence.pricePerTon') }}</span>
                          <span class="value">{{ formattedPricePerTon }}</span>
                        </div>
                        <div class="mp-field">
                          <span class="label">{{ $t('dueDiligence.calculatedAmount') }}</span>
                          <span class="value amount-emphasis">{{ formattedAmount }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="invoice-letterhead__footer">{{ $t('dueDiligence.invoiceFooterNote') }}</div>
                  </div>
                </div>
              </div>
              <div v-if="needsResponse" class="mp-note">
                <v-icon small color="orange darken-2" class="mr-2">mdi-information-outline</v-icon>
                <span>{{ $t('dueDiligence.reviewAcceptNote') }}</span>
              </div>
              <div class="mp-actions">
                <v-spacer></v-spacer>
                <v-btn v-if="highestReachableStep > 1" depressed color="primary" @click="goForward">
                  {{ needsResponse ? $t('dueDiligence.continueToAccept') : $t('dueDiligence.continue') }}
                </v-btn>
              </div>
            </div>

            <!-- STEP 2: Become the Payer -->
            <div v-if="step === 2">
              <div class="mp-card">
                <div class="mp-card-header">
                  <v-icon color="primary">mdi-account-check-outline</v-icon>
                  <div>
                    <div class="mp-card-title">2. {{ $t('dueDiligence.stepConfirmResponsibility') }}</div>
                    <div class="mp-card-subtitle">{{ $t('dueDiligence.confirmResponsibilityHint') }}</div>
                  </div>
                </div>
                <div class="mp-card-body">
                  <template v-if="needsResponse">
                    <div class="mp-accept-box">
                      <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                      <div>
                        <div>{{ $t('dueDiligence.acceptResponsibilityStatement') }}</div>
                        <div class="text-caption grey--text mt-1">{{ $t('dueDiligence.acceptResponsibilityNote') }}</div>
                      </div>
                    </div>
                    <v-checkbox
                      v-model="acceptChecked"
                      :label="$t('dueDiligence.acceptResponsibilityCheckboxLabel')"
                      hide-details
                      class="mt-3"
                      :disabled="responding === 'accept'"
                    ></v-checkbox>
                  </template>
                  <template v-else-if="isCurrentUserPayer">
                    <div class="mp-info-box mp-info-box--success">
                      <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                      <span>{{ $t('dueDiligence.paymentResponsibilityAccepted') }}</span>
                    </div>
                  </template>
                  <template v-else>
                    <div class="mp-info-box">
                      <v-icon color="grey" class="mr-2">mdi-information-outline</v-icon>
                      <span>{{ $t('dueDiligence.paymentUnavailableTooltip') }}</span>
                    </div>
                  </template>
                </div>
              </div>
              <div class="mp-actions">
                <v-btn outlined color="primary" :disabled="busy" @click="goBack">{{ $t('dueDiligence.back') }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  v-if="needsResponse"
                  depressed
                  color="primary"
                  :loading="responding === 'accept'"
                  :disabled="!acceptChecked"
                  @click="acceptResponsibility"
                >
                  {{ $t('dueDiligence.paymentResponsibilityTitle') }}
                </v-btn>
                <v-btn
                  v-else-if="highestReachableStep > 2"
                  depressed
                  color="primary"
                  @click="goForward"
                >
                  {{ $t('dueDiligence.continueToPayment') }}
                </v-btn>
              </div>
            </div>

            <!-- STEP 3: Make Payment -->
            <div v-if="step === 3">
              <div v-if="showUploadWidget" class="mp-card">
                <div class="mp-card-header">
                  <v-icon color="primary">mdi-wallet-outline</v-icon>
                  <div>
                    <div class="mp-card-title">3. {{ $t('dueDiligence.selectPaymentMethod') }}</div>
                    <div class="mp-card-subtitle">{{ $t('dueDiligence.selectPaymentMethodHint') }}</div>
                  </div>
                </div>
                <div class="mp-card-body">
                  <div class="pay-method-grid">
                    <button
                      type="button"
                      class="pay-method-card"
                      :class="{ 'pay-method-card--selected': paymentMethod === 'bank' }"
                      @click="paymentMethod = 'bank'"
                    >
                      <v-icon :color="paymentMethod === 'bank' ? 'primary' : 'grey'">mdi-bank-outline</v-icon>
                      <div class="pay-method-card__text">
                        <div class="pay-method-card__title">{{ $t('dueDiligence.paymentMethodBank') }}</div>
                        <div class="pay-method-card__desc">{{ $t('dueDiligence.paymentMethodBankDesc') }}</div>
                      </div>
                    </button>
                    <div class="pay-method-card pay-method-card--disabled">
                      <span class="pay-method-soon">{{ $t('dueDiligence.midtransComingSoon') }}</span>
                      <v-icon color="grey">mdi-credit-card-outline</v-icon>
                      <div class="pay-method-card__text">
                        <div class="pay-method-card__title">{{ $t('dueDiligence.paymentMethodMidtrans') }}</div>
                        <div class="pay-method-card__desc">{{ $t('dueDiligence.paymentMethodMidtransDesc') }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="showUploadWidget && paymentMethod === 'bank'" class="mp-card">
                <div class="mp-card-header">
                  <v-icon color="primary">mdi-bank-transfer</v-icon>
                  <div>
                    <div class="mp-card-title">{{ $t('dueDiligence.bankTransferDetails') }}</div>
                    <div class="mp-card-subtitle">{{ $t('dueDiligence.transferThenUploadHint') }}</div>
                  </div>
                </div>
                <div class="mp-card-body">
                  <div class="bank-box">
                    <div class="bank-row" v-for="row in bankDetailRows" :key="row.key">
                      <span class="label">{{ row.label }}</span>
                      <span class="bank-row__value">
                        <span class="value">{{ row.value }}</span>
                        <v-btn
                          v-if="row.copyable"
                          icon
                          x-small
                          class="ml-1"
                          :title="$t('dueDiligence.copied')"
                          @click="copyBankValue(row.value, row.key)"
                        >
                          <v-icon x-small :color="copiedField === row.key ? 'success' : 'primary'">
                            {{ copiedField === row.key ? 'mdi-check' : 'mdi-content-copy' }}
                          </v-icon>
                        </v-btn>
                      </span>
                    </div>
                  </div>
                  <div class="how-to-pay mt-4">
                    <div class="how-to-pay__label">{{ $t('dueDiligence.howToPay') }}</div>
                    <div class="how-to-pay__chips">
                      <span class="how-to-pay__chip"><v-icon x-small color="success">mdi-check</v-icon>{{ $t('dueDiligence.howToPayMobile') }}</span>
                      <span class="how-to-pay__chip"><v-icon x-small color="success">mdi-check</v-icon>{{ $t('dueDiligence.howToPayInternet') }}</span>
                      <span class="how-to-pay__chip"><v-icon x-small color="success">mdi-check</v-icon>{{ $t('dueDiligence.howToPayAtm') }}</span>
                    </div>
                  </div>
                  <div class="mp-swift-note mt-3">
                    <v-icon small color="primary" class="mr-2">mdi-earth</v-icon>
                    <span>{{ $t('dueDiligence.internationalWireNote') }}</span>
                  </div>
                </div>
              </div>

              <div class="mp-card">
                <div class="mp-card-header">
                  <v-icon color="primary">mdi-cloud-upload-outline</v-icon>
                  <div>
                    <div class="mp-card-title">{{ showUploadWidget ? $t('dueDiligence.uploadBankReceiptTitle') : $t('dueDiligence.submittedPaymentProof') }}</div>
                    <div class="mp-card-subtitle">{{ showUploadWidget ? $t('dueDiligence.uploadBankReceiptHint') : submittedByHint }}</div>
                  </div>
                </div>
                <div class="mp-card-body">
                  <div v-if="paymentStatus === 'rejected'" class="mp-reject-reason mb-4">
                    <div class="mp-reject-reason-title">{{ $t('dueDiligence.paymentRejected') }}</div>
                    <div>{{ notReceivedNote }}</div>
                  </div>
                  <template v-if="showUploadWidget">
                    <div
                      v-if="!receiptFile"
                      class="receipt-dropzone"
                      :class="{
                        'receipt-dropzone--active': dragging,
                        'receipt-dropzone--disabled': uploading || submitting,
                      }"
                      @dragenter.prevent="onDragEnter"
                      @dragover.prevent="onDragOver"
                      @dragleave.prevent="onDragLeave"
                      @drop.prevent="onDrop"
                    >
                      <div class="receipt-dropzone__content">
                        <v-icon size="44" :color="dragging ? 'primary' : 'grey lighten-1'">mdi-tray-arrow-up</v-icon>
                        <div class="receipt-dropzone__title primary--text">
                          {{ $t('dueDiligence.dragDropFileHere') }}
                        </div>
                        <div class="receipt-dropzone__subtitle">
                          {{ $t('dueDiligence.clickToBrowse') }}
                        </div>
                        <div class="receipt-dropzone__formats text-caption text--secondary mt-2">
                          {{ $t('dueDiligence.supportedFormatsMax5MB') }}
                        </div>
                      </div>
                      <input
                        ref="receiptInput"
                        type="file"
                        class="receipt-dropzone__input"
                        accept="image/*,.pdf"
                        :disabled="uploading || submitting"
                        @change="onReceiptInputChange"
                      />
                    </div>

                    <div v-else class="receipt-file-card">
                      <div class="receipt-file-card__icon">
                        <v-icon color="primary">{{ receiptFileIcon }}</v-icon>
                      </div>
                      <div class="receipt-file-card__meta">
                        <div class="receipt-file-card__name">{{ receiptFile.name }}</div>
                        <div class="receipt-file-card__size text-caption text--secondary">
                          {{ formatFileSize(receiptFile.size) }}
                        </div>
                      </div>
                      <v-btn icon small :disabled="uploading || submitting" @click="clearReceiptFile">
                        <v-icon small color="error">mdi-close</v-icon>
                      </v-btn>
                    </div>

                    <div v-if="localReceiptPreviewUrl" class="receipt-preview mt-3">
                      <div v-if="isLocalImageReceipt" class="receipt-preview-frame">
                        <img :src="localReceiptPreviewUrl" :alt="$t('dueDiligence.paymentReceipt')" class="receipt-image" />
                      </div>
                      <div v-else-if="isLocalPdfReceipt" class="receipt-preview-frame">
                        <iframe :src="localReceiptPreviewUrl" class="receipt-iframe" title="payment-receipt-preview"></iframe>
                      </div>
                    </div>

                    <v-textarea
                      v-model="transactionNote"
                      :label="$t('dueDiligence.paymentTransactionNote')"
                      :placeholder="$t('dueDiligence.paymentTransactionNotePlaceholder')"
                      outlined
                      rows="2"
                      :disabled="submitting"
                      class="mt-2"
                      counter="500"
                      maxlength="500"
                    ></v-textarea>
                  </template>

                  <template v-else-if="hasSubmittedReceipt">
                    <div class="mp-grid-2">
                      <div class="mp-field">
                        <span class="label">{{ $t('dueDiligence.receiptSubmittedBy') }}</span>
                        <span class="value">{{ submittedByLabel }}</span>
                      </div>
                      <div class="mp-field">
                        <span class="label">{{ $t('dueDiligence.receiptSubmittedAt') }}</span>
                        <span class="value">{{ submittedAtLabel }}</span>
                      </div>
                      <div class="mp-field full" v-if="payment && payment.transaction_note">
                        <span class="label">{{ $t('dueDiligence.paymentProofNote') }}</span>
                        <span class="value">{{ payment.transaction_note }}</span>
                      </div>
                    </div>
                    <div class="receipt-preview mt-4">
                      <div class="receipt-preview-header">
                        <span class="receipt-preview-title">{{ $t('dueDiligence.receiptPreview') }}</span>
                        <v-btn
                          small
                          text
                          color="primary"
                          :loading="loadingReceipt"
                          :disabled="!receiptUrl"
                          @click="openReceiptInNewTab"
                        >
                          <v-icon left small>mdi-open-in-new</v-icon>
                          {{ $t('dueDiligence.openSubmittedReceipt') }}
                        </v-btn>
                      </div>
                      <div class="receipt-preview-frame" v-if="loadingReceipt">
                        <div class="receipt-loading">
                          <v-progress-circular indeterminate color="primary" size="36"></v-progress-circular>
                          <span>{{ $t('dueDiligence.loadingReceipt') }}</span>
                        </div>
                      </div>
                      <div class="receipt-preview-frame" v-else-if="receiptLoadError">
                        <div class="receipt-empty">
                          <v-icon large color="grey">mdi-file-alert-outline</v-icon>
                          <p>{{ $t('dueDiligence.receiptPreviewUnavailable') }}</p>
                          <v-btn small outlined color="primary" @click="loadReceiptPreview">
                            {{ $t('dueDiligence.receiptPreviewRetry') }}
                          </v-btn>
                        </div>
                      </div>
                      <div class="receipt-preview-frame" v-else-if="receiptUrl && isImageReceipt">
                        <img :src="receiptUrl" :alt="$t('dueDiligence.paymentReceipt')" class="receipt-image" />
                      </div>
                      <div class="receipt-preview-frame" v-else-if="receiptUrl && isPdfReceipt">
                        <iframe :src="receiptUrl" class="receipt-iframe" title="payment-receipt"></iframe>
                      </div>
                      <div class="receipt-preview-frame" v-else-if="receiptUrl">
                        <div class="receipt-empty">
                          <v-icon large color="primary">mdi-file-document-outline</v-icon>
                          <p>{{ $t('dueDiligence.receiptPreviewUnsupported') }}</p>
                          <v-btn small depressed color="primary" @click="openReceiptInNewTab">
                            {{ $t('dueDiligence.openSubmittedReceipt') }}
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </template>

                  <template v-else>
                    <div class="mp-info-box">
                      <span>{{ $t('dueDiligence.receiptNotSubmittedYet') }}</span>
                    </div>
                  </template>
                </div>
              </div>
              <div class="mp-actions">
                <v-btn outlined color="primary" :disabled="busy" @click="goBack">{{ $t('dueDiligence.back') }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  v-if="showUploadWidget"
                  depressed
                  color="primary"
                  :loading="submitting"
                  :disabled="!receiptFile || uploading"
                  @click="submitPayment"
                >
                  {{ $t('dueDiligence.submitForVerification') }}
                </v-btn>
                <v-btn v-else-if="highestReachableStep > 3" depressed color="primary" @click="goForward">
                  {{ $t('dueDiligence.continue') }}
                </v-btn>
              </div>
            </div>

            <!-- STEP 4: Complete -->
            <div v-if="step === 4">
              <div class="mp-card mp-complete-card">
                <template v-if="paymentStatus === 'verified'">
                  <v-icon size="56" color="success">mdi-check-circle</v-icon>
                  <div class="mp-complete-title">{{ $t('dueDiligence.paymentVerified') }}</div>
                  <div class="mp-complete-desc">{{ $t('dueDiligence.paymentVerifiedMessage') }}</div>
                </template>
                <template v-else-if="paymentStatus === 'rejected'">
                  <v-icon size="56" color="error">mdi-close-circle</v-icon>
                  <div class="mp-complete-title">{{ $t('dueDiligence.paymentRejected') }}</div>
                  <div class="mp-complete-desc">{{ $t('dueDiligence.paymentRejectedMessage') }}</div>
                  <div class="mp-reject-reason" v-if="notReceivedNote">{{ notReceivedNote }}</div>
                  <v-btn outlined color="primary" class="mt-4" @click="goToStep(3)">
                    {{ $t('dueDiligence.reUploadReceipt') }}
                  </v-btn>
                </template>
                <template v-else-if="hasSubmittedReceipt">
                  <v-icon size="56" color="primary">mdi-clock-outline</v-icon>
                  <div class="mp-complete-title">{{ $t('dueDiligence.paymentSubmittedTitle') }}</div>
                  <div class="mp-complete-desc">{{ $t('dueDiligence.paymentSubmittedMessage') }}</div>
                </template>
                <template v-else-if="canSubmit">
                  <v-icon size="56" color="primary">mdi-cloud-upload-outline</v-icon>
                  <div class="mp-complete-title">{{ $t('dueDiligence.stepMakePayment') }}</div>
                  <div class="mp-complete-desc">{{ $t('dueDiligence.uploadBankReceiptHint') }}</div>
                  <v-btn depressed color="primary" class="mt-4" @click="goToStep(3)">
                    {{ $t('dueDiligence.stepMakePayment') }}
                  </v-btn>
                </template>
                <template v-else>
                  <v-icon size="56" color="grey">mdi-information-outline</v-icon>
                  <div class="mp-complete-desc">{{ $t('dueDiligence.receiptNotSubmittedYet') }}</div>
                </template>
              </div>
              <div class="mp-actions">
                <v-btn outlined color="primary" :disabled="busy" @click="goBack">{{ $t('dueDiligence.back') }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn depressed color="primary" @click="closeModal">{{ $t('close') }}</v-btn>
              </div>
            </div>

          </div>

          <div class="mp-sidebar">
            <div class="mp-side-card mp-side-card--status">
              <div class="mp-side-card-header">
                <v-icon color="orange darken-2">mdi-clock-outline</v-icon>
                <span class="mp-side-card-title">{{ $t('dueDiligence.paymentStatusTitle') }}</span>
              </div>
              <span class="mp-status-chip" :class="'mp-status-chip--' + paymentStatus">{{ paymentStatusLabel }}</span>
              <p class="mp-side-hint">{{ paymentStatusHint }}</p>
            </div>

            <div class="mp-side-card">
              <div class="mp-side-card-header">
                <v-icon color="primary">mdi-bank</v-icon>
                <span class="mp-side-card-title">{{ $t('dueDiligence.paymentInstructionsTitle') }}</span>
              </div>
              <div class="mp-instruction">
                <div class="mp-instruction-num">1</div>
                <div>
                  <div class="mp-instruction-title">{{ $t('dueDiligence.paymentInstructionsStep1Title') }}</div>
                  <div class="mp-instruction-desc">{{ $t('dueDiligence.paymentInstructionsStep1Desc', { amount: formattedAmount }) }}</div>
                  <v-btn outlined x-small color="primary" class="mt-2" @click="showBankDetails = true">
                    <v-icon left x-small>mdi-bank-outline</v-icon>{{ $t('dueDiligence.viewBankDetails') }}
                  </v-btn>
                </div>
              </div>
              <div class="mp-instruction">
                <div class="mp-instruction-num">2</div>
                <div>
                  <div class="mp-instruction-title">{{ $t('dueDiligence.paymentInstructionsStep2Title') }}</div>
                  <div class="mp-instruction-desc">{{ $t('dueDiligence.paymentInstructionsStep2Desc') }}</div>
                </div>
              </div>
              <div class="mp-instruction mp-instruction--last">
                <div class="mp-instruction-num">3</div>
                <div>
                  <div class="mp-instruction-title">{{ $t('dueDiligence.paymentInstructionsStep3Title') }}</div>
                  <div class="mp-instruction-desc">{{ $t('dueDiligence.paymentInstructionsStep3Desc') }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Bank Details sub-dialog -->
    <v-dialog v-model="showBankDetails" max-width="520">
      <v-card>
        <v-card-title>{{ $t('dueDiligence.bankDetails') }}</v-card-title>
        <v-card-text>
          <div class="bank-box">
            <div class="bank-row" v-for="row in bankDetailRows" :key="'dlg-' + row.key">
              <span class="label">{{ row.label }}</span>
              <span class="value">{{ row.value }}</span>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="showBankDetails = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import html2pdf from 'html2pdf.js';
import printJS from 'print-js';
import moment from 'moment';
import { normalizeDdsStatus, showDdsPaymentBankDetails as isDdsPaymentBankDetailsEnabled, resolveDdsExporterPayerFields, DDS_PAYMENT_BANK_DETAILS } from '@/utils';
import S3UploadService from '@/_services/S3UploadService';

export default {
  name: 'PaymentModal',
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    item: {
      type: Object,
      default: null,
    },
    bankDetails: {
      type: Object,
      default: () => ({ ...DDS_PAYMENT_BANK_DETAILS }),
    },
    payerInfo: {
      type: Object,
      default: () => ({
        name: '-',
        companyId: '-',
        contact: '-',
      }),
    },
    issuerName: {
      type: String,
      default: 'PT Surveyor Indonesia',
    },
    // Whether the current user still needs to accept/decline payment responsibility.
    needsResponse: {
      type: Boolean,
      default: false,
    },
    // Whether the current user is allowed to submit a payment receipt for this item.
    canSubmit: {
      type: Boolean,
      default: false,
    },
    // Whether the current user is allowed to release (exit) payment responsibility for this item.
    canRelease: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      step: 1,
      acceptChecked: false,
      responding: null,
      ddsPaymentBankDetailsVisible: isDdsPaymentBankDetailsEnabled(),
      showBankDetails: false,
      downloading: false,
      capturingInvoice: false,
      loadingReceipt: false,
      receiptUrl: null,
      receiptLoadError: false,
      localReceiptPreviewUrl: null,
      receiptFile: null,
      transactionNote: '',
      uploading: false,
      submitting: false,
      dragging: false,
      dragDepth: 0,
      paymentMethod: 'bank',
      copiedField: null,
    };
  },
  computed: {
    busy() {
      return this.submitting || !!this.responding;
    },
    busyMessage() {
      if (this.responding === 'accept') return this.$t('dueDiligence.acceptingPaymentResponsibility');
      if (this.uploading) return this.$t('dueDiligence.uploadingReceipt');
      return this.$t('dueDiligence.submittingPayment');
    },
    steps() {
      return [
        { title: this.$t('dueDiligence.stepReviewDetails'), desc: this.$t('dueDiligence.stepReviewDetailsDesc') },
        { title: this.$t('dueDiligence.stepConfirmResponsibility'), desc: this.$t('dueDiligence.stepConfirmResponsibilityDesc') },
        { title: this.$t('dueDiligence.stepMakePayment'), desc: this.$t('dueDiligence.stepMakePaymentDesc') },
        {
          title: this.$t('dueDiligence.stepComplete'),
          desc: this.hasSubmittedReceipt && this.paymentStatus === 'verified'
            ? this.$t('dueDiligence.stepCompleteDescDone')
            : this.$t('dueDiligence.stepCompleteDescPending'),
        },
      ];
    },
    payment() {
      return this.item?.payment || null;
    },
    hasSubmittedReceipt() {
      return !!(this.payment?.receipt_s3_key);
    },
    paymentStatus() {
      return normalizeDdsStatus(this.payment?.status);
    },
    paymentStatusLabel() {
      const map = {
        pending: this.$t('dueDiligence.paymentDue'),
        invoice_generated: this.$t('dueDiligence.invoiceGeneratedStatus'),
        reported: this.$t('dueDiligence.paymentPendingVerification'),
        verified: this.$t('dueDiligence.paymentVerified'),
        rejected: this.$t('dueDiligence.paymentRejected'),
      };
      return map[this.paymentStatus] || (this.payment?.status || '-');
    },
    paymentStatusHint() {
      if (this.paymentStatus === 'verified') return this.$t('dueDiligence.paymentStatusHintVerified');
      if (this.paymentStatus === 'rejected') return this.$t('dueDiligence.paymentStatusHintRejected');
      if (this.paymentStatus === 'reported') return this.$t('dueDiligence.paymentStatusHintReported');
      return this.$t('dueDiligence.paymentStatusHintWaiting');
    },
    showUploadWidget() {
      return this.canSubmit && (!this.hasSubmittedReceipt || this.paymentStatus === 'rejected');
    },
    isCurrentUserPayer() {
      return this.canSubmit || this.canRelease || this.isSubmittedByCurrentUser;
    },
    // Furthest step the user is allowed to open, based on payment progress — not the page they happen to be on.
    highestReachableStep() {
      // A rejected receipt must not skip Become the Payer or jump to Complete.
      if (this.needsResponse) return 2;
      if (this.paymentStatus === 'verified') return 4;
      if (this.paymentStatus === 'rejected') return 3;
      if (this.hasSubmittedReceipt) return 4;
      if (this.canSubmit || this.canRelease) return 3;
      return 1;
    },
    country() {
      return this.item?.countryOfEntry || '';
    },
    invoiceNumber() {
      if (this.payment?.invoice_number) return this.payment.invoice_number;
      if (this.item?.id && this.payment?.id) return `INV-${this.item.id}-${this.payment.id}`;
      if (this.item?.id) return `INV-${this.item.id}`;
      return '-';
    },
    ddsReference() {
      const eudr = this.item?.EUDRReferenceNumber;
      const internal = this.item?.internalReferenceNumber;
      if (eudr && internal) return `${eudr} / ${internal}`;
      return eudr || internal || '-';
    },
    invoiceDate() {
      const raw = this.payment?.invoice_generated_at
        || this.payment?.updatedAt
        || this.payment?.createdAt
        || this.item?.updatedAt;
      if (!raw) return moment().format('DD MMM YYYY');
      return moment(raw).format('DD MMM YYYY');
    },
    currentUserId() {
      return this.$store?.getters?.getUser?.id ?? null;
    },
    isSubmittedByCurrentUser() {
      if (!this.payment?.payer_cf_id || !this.currentUserId) return false;
      return Number(this.payment.payer_cf_id) === Number(this.currentUserId);
    },
    submittedExporter() {
      const payerCfId = this.payment?.payer_cf_id;
      if (!payerCfId || !this.item?.ddsReportExporters?.length) return null;
      return this.item.ddsReportExporters.find(
        (exporter) => Number(exporter.exporter_cf_id) === Number(payerCfId)
      ) || null;
    },
    submittedByLabel() {
      if (!this.hasSubmittedReceipt) return '-';
      if (this.isSubmittedByCurrentUser) {
        return this.$t('dueDiligence.receiptSubmittedByYou');
      }
      const exporter = this.submittedExporter;
      if (exporter) {
        const resolved = resolveDdsExporterPayerFields(exporter, this.item?.companyID);
        if (resolved.contact !== '-') return resolved.contact;
        if (resolved.name !== '-') return resolved.name;
      }
      if (this.payerInfo?.contact && this.payerInfo.contact !== '-') return this.payerInfo.contact;
      if (this.payerInfo?.name && this.payerInfo.name !== '-') return this.payerInfo.name;
      return this.$t('dueDiligence.receiptSubmittedByAnotherExporter');
    },
    submittedByHint() {
      return this.isSubmittedByCurrentUser
        ? this.$t('dueDiligence.paymentProofHintYou')
        : this.$t('dueDiligence.paymentProofHintOther');
    },
    submittedAtLabel() {
      const raw = this.payment?.reported_at;
      if (!raw) return '-';
      return moment(raw).format('DD MMM YYYY, HH:mm');
    },
    payer() {
      return {
        name: this.payerInfo?.name || '-',
        companyId: this.payerInfo?.companyId || this.item?.companyID || '-',
        contact: this.payerInfo?.contact || '-',
      };
    },
    productName() {
      const detail = this.item?.product_detail;
      if (!detail) return this.item?.product || '-';
      return detail.code ? `${detail.code} - ${detail.name}` : detail.name;
    },
    netMassTons() {
      if (this.payment?.net_mass_tons_snapshot != null) {
        return Number(this.payment.net_mass_tons_snapshot);
      }
      const kg = this.item?.productNetMass;
      if (kg == null || isNaN(kg)) return null;
      return Number(kg) / 1000;
    },
    formattedNetMass() {
      if (this.netMassTons == null) return '-';
      return this.netMassTons.toLocaleString(undefined, { maximumFractionDigits: 4 });
    },
    currency() {
      return this.payment?.currency || 'IDR';
    },
    pricePerTon() {
      if (this.payment?.price_per_ton_snapshot != null) {
        return Number(this.payment.price_per_ton_snapshot);
      }
      if (this.payment?.amount != null && this.netMassTons) {
        return Number(this.payment.amount) / this.netMassTons;
      }
      return null;
    },
    formattedPricePerTon() {
      if (this.pricePerTon == null) return '-';
      return `${this.currency} ${this.pricePerTon.toLocaleString()}`.trim();
    },
    formattedAmount() {
      if (this.payment?.amount == null) return '-';
      return `${this.currency} ${Number(this.payment.amount).toLocaleString()}`.trim();
    },
    receiptFileIcon() {
      const name = this.receiptFile?.name || '';
      return name.toLowerCase().endsWith('.pdf') ? 'mdi-file-pdf-box' : 'mdi-file-image';
    },
    receiptKeyLower() {
      return String(this.payment?.receipt_s3_key || '').toLowerCase();
    },
    isImageReceipt() {
      return /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(this.receiptKeyLower);
    },
    isPdfReceipt() {
      return /\.pdf(\?|$)/i.test(this.receiptKeyLower);
    },
    isLocalImageReceipt() {
      return !!(this.receiptFile && (this.receiptFile.type || '').startsWith('image/'));
    },
    isLocalPdfReceipt() {
      return !!this.receiptFile?.name?.toLowerCase().endsWith('.pdf');
    },
    notReceivedNote() {
      return (this.payment?.rejection_reason || '').trim()
        || this.$t('dueDiligence.paymentNotReceivedDefaultMessage');
    },
    resolvedBankDetails() {
      return {
        ...DDS_PAYMENT_BANK_DETAILS,
        ...(this.bankDetails || {}),
      };
    },
    bankDetailRows() {
      const details = this.resolvedBankDetails;
      return [
        { key: 'purpose', label: this.$t('dueDiligence.paymentPurpose'), value: details.purpose, copyable: false },
        { key: 'accountName', label: this.$t('dueDiligence.accountName'), value: details.accountName, copyable: true },
        { key: 'accountNumber', label: this.$t('dueDiligence.accountNumber'), value: details.accountNumber, copyable: true },
        { key: 'bankName', label: this.$t('dueDiligence.bankName'), value: details.bankName, copyable: false },
        { key: 'bankBranch', label: this.$t('dueDiligence.bankBranch'), value: details.bankBranch, copyable: false },
        { key: 'swiftCode', label: this.$t('dueDiligence.swiftBicCode'), value: details.swiftCode, copyable: true },
      ];
    },
  },
  methods: {
    isStepComplete(n) {
      if (n === 1) return this.step > 1 || this.highestReachableStep > 1;
      if (n === 2) return !this.needsResponse;
      if (n === 3) return this.hasSubmittedReceipt && this.paymentStatus !== 'rejected';
      if (n === 4) return this.paymentStatus === 'verified';
      return false;
    },
    stepStatus(n) {
      if (n === this.step) return 'current';
      if (this.isStepComplete(n)) return 'done';
      return 'upcoming';
    },
    isStepNavigable(n) {
      return n !== this.step && n >= 1 && n <= this.highestReachableStep;
    },
    goToStep(n) {
      if (this.busy || !this.isStepNavigable(n)) return;
      this.step = n;
    },
    goBack() {
      if (this.busy || this.step <= 1) return;
      this.step -= 1;
    },
    goForward() {
      if (this.busy) return;
      const next = this.step + 1;
      if (next <= this.highestReachableStep) this.step = next;
    },
    computeStepFromState() {
      if (this.needsResponse) return 1;
      if (this.paymentStatus === 'verified') return 4;
      if (this.paymentStatus === 'rejected') return 3;
      if (this.hasSubmittedReceipt) return 4;
      if (this.canSubmit) return 3;
      return 1;
    },
    resetWizard() {
      this.acceptChecked = false;
      this.responding = null;
      this.paymentMethod = 'bank';
      this.copiedField = null;
      this.step = this.computeStepFromState();
    },
    acceptResponsibility() {
      if (this.busy || !this.acceptChecked) return;
      this.responding = 'accept';
      this.$emit('respond', {
        accept: true,
        done: (error) => {
          this.responding = null;
          if (!error) {
            // Wait for the updated canSubmit prop (parent just patched the item) to flush
            // before rendering step 3, so it doesn't briefly show the stale read-only view.
            this.$nextTick(() => {
              this.step = 3;
            });
          }
        },
      });
    },
    closeModal() {
      this.resetForm();
      this.$emit('close');
    },
    async loadReceiptPreview() {
      if (!this.payment?.receipt_s3_key) {
        this.receiptUrl = null;
        this.receiptLoadError = false;
        return;
      }
      this.loadingReceipt = true;
      this.receiptLoadError = false;
      try {
        this.receiptUrl = await S3UploadService.getFileUrl(this.payment.receipt_s3_key);
      } catch (error) {
        console.error('Error loading payment receipt preview:', error);
        this.receiptUrl = null;
        this.receiptLoadError = true;
      } finally {
        this.loadingReceipt = false;
      }
    },
    openReceiptInNewTab() {
      if (!this.receiptUrl) return;
      window.open(this.receiptUrl, '_blank');
    },
    viewInvoice() {
      if (this.busy) return;
      this.step = 1;
    },
    printInvoice() {
      const element = this.$refs.invoiceDocument;
      if (!element) return;
      printJS({
        printable: 'dds-invoice-document',
        type: 'html',
        documentTitle: this.invoiceNumber || 'DDS-Invoice',
        scanStyles: false,
        css: null,
        style: `
          body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; }
          .invoice-letterhead { padding: 16px; }
          .invoice-letterhead__top { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding-bottom: 16px; border-bottom: 2px solid #184980; margin-bottom: 18px; }
          .invoice-letterhead__logo { height: 40px; width: auto; max-width: 280px; object-fit: contain; }
          .invoice-letterhead__badge { background: #184980; color: #fff; font-size: 13px; font-weight: 600; padding: 10px 18px; border-radius: 8px; }
          .invoice-letterhead__meta { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 24px; background: #e0f0ff; border-radius: 8px; padding: 14px 16px; margin-bottom: 20px; }
          .invoice-section { margin-bottom: 18px; }
          .invoice-section__title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #184980; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid #eef1f6; }
          .invoice-section--highlight { background: #e0f0ff; border-radius: 8px; padding: 14px 16px; }
          .invoice-letterhead__footer { margin-top: 22px; padding-top: 14px; border-top: 1px solid #eef1f6; font-size: 11px; color: #78829a; }
          .mp-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 20px; }
          .mp-field { display: flex; flex-direction: column; }
          .label { font-size: 11px; color: #666; margin-bottom: 2px; }
          .value { font-size: 14px; font-weight: 600; }
          .amount-emphasis { color: #184980; font-size: 16px; }
        `,
      });
    },
    async downloadPdf() {
      if (this.busy && !this.downloading) return;
      this.downloading = true;
      const needsOffscreen = this.step !== 1;
      if (needsOffscreen) {
        this.capturingInvoice = true;
        await this.$nextTick();
      }
      try {
        const element = this.$refs.invoiceDocument;
        if (!element) return;
        const filename = `${this.invoiceNumber || 'DDS-Invoice'}.pdf`;
        await html2pdf()
          .set({
            margin: [0.4, 0.4, 0.4, 0.4],
            filename,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
          })
          .from(element)
          .save();
      } catch (error) {
        console.error('Error downloading invoice PDF:', error);
        this.$toast?.error?.(error?.message || this.$t('somethingWentWrong'));
      } finally {
        this.capturingInvoice = false;
        this.downloading = false;
      }
    },
    onDragEnter() {
      if (this.uploading || this.submitting) return;
      this.dragDepth += 1;
      this.dragging = true;
    },
    onDragOver() {
      if (this.uploading || this.submitting) return;
      this.dragging = true;
    },
    onDragLeave() {
      if (this.uploading || this.submitting) return;
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) this.dragging = false;
    },
    onDrop(event) {
      if (this.uploading || this.submitting) return;
      this.dragging = false;
      this.dragDepth = 0;
      const file = event.dataTransfer?.files?.[0];
      if (file) this.setReceiptFile(file);
    },
    onReceiptInputChange(event) {
      const file = event.target?.files?.[0];
      if (file) this.setReceiptFile(file);
      if (this.$refs.receiptInput) this.$refs.receiptInput.value = '';
    },
    setReceiptFile(file) {
      if (!this.isAllowedReceiptFile(file)) {
        this.$emit('error', new Error(`${this.$t('dueDiligence.uploadReceiptHint')}`));
        return;
      }
      this.clearLocalPreview();
      this.receiptFile = file;
      this.localReceiptPreviewUrl = URL.createObjectURL(file);
    },
    isAllowedReceiptFile(file) {
      if (!file?.name) return false;
      const lowerName = file.name.toLowerCase();
      if (lowerName.endsWith('.pdf')) return true;
      return (file.type || '').startsWith('image/');
    },
    clearLocalPreview() {
      if (this.localReceiptPreviewUrl) {
        URL.revokeObjectURL(this.localReceiptPreviewUrl);
        this.localReceiptPreviewUrl = null;
      }
    },
    clearReceiptFile() {
      this.clearLocalPreview();
      this.receiptFile = null;
      if (this.$refs.receiptInput) this.$refs.receiptInput.value = '';
    },
    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    resetForm() {
      this.clearLocalPreview();
      this.receiptFile = null;
      this.transactionNote = '';
      this.uploading = false;
      this.submitting = false;
      this.dragging = false;
      this.dragDepth = 0;
      this.receiptUrl = null;
      this.receiptLoadError = false;
      this.loadingReceipt = false;
      this.paymentMethod = 'bank';
      this.copiedField = null;
      if (this.$refs.receiptInput) this.$refs.receiptInput.value = '';
    },
    copyBankValue(value, field) {
      if (!value || !navigator.clipboard) return;
      navigator.clipboard.writeText(String(value)).then(() => {
        this.copiedField = field;
        clearTimeout(this._copyTimer);
        this._copyTimer = setTimeout(() => {
          this.copiedField = null;
        }, 1600);
      }).catch(() => {});
    },
    async submitPayment() {
      if (!this.receiptFile || this.submitting) return;

      this.submitting = true;
      try {
        this.uploading = true;
        const { key } = await S3UploadService.singleUpload(this.receiptFile, 'dds-payment-receipts');
        this.uploading = false;

        this.$emit('confirm', {
          receiptS3Key: key,
          transactionNote: this.transactionNote?.trim() || undefined,
          done: (error) => {
            this.submitting = false;
            this.uploading = false;
            if (!error) {
              this.clearLocalPreview();
              this.receiptFile = null;
              this.transactionNote = '';
              this.$nextTick(() => {
                this.step = 4;
                this.loadReceiptPreview();
              });
            }
          },
        });
      } catch (error) {
        this.uploading = false;
        this.submitting = false;
        this.$emit('error', error);
      }
    },
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        this.resetWizard();
        this.$nextTick(() => {
          this.loadReceiptPreview();
        });
      } else {
        this.resetForm();
      }
    },
    'payment.receipt_s3_key'(newKey, oldKey) {
      if (this.dialog && newKey && newKey !== oldKey) {
        this.loadReceiptPreview();
      }
    },
  },
  beforeDestroy() {
    this.clearLocalPreview();
    clearTimeout(this._copyTimer);
  },
};
</script>

<style scoped>
.mp-card-root {
  overflow: hidden;
}

.mp-header {
  display: flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(90deg, #0d47a1, #1565c0);
  padding: 18px 24px;
  color: #fff;
  flex-wrap: wrap;
}

.mp-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.mp-header-btn {
  color: #fff !important;
  border-color: rgba(255, 255, 255, 0.7) !important;
  text-transform: none;
  letter-spacing: 0;
}

.mp-header-btn--active {
  background: rgba(255, 255, 255, 0.16) !important;
  border-color: #fff !important;
}

.mp-header-btn--danger {
  background: #d32f2f !important;
  color: #fff !important;
  border-color: #d32f2f !important;
}

.mp-header-btn--danger:hover {
  background: #b71c1c !important;
}

.invoice-capture-offscreen {
  position: absolute !important;
  left: -10000px !important;
  top: 0 !important;
  width: 794px !important;
  pointer-events: none;
  z-index: -1;
}

.mp-header-title {
  font-size: 19px;
  font-weight: 700;
}

.mp-header-subtitle {
  font-size: 13px;
  opacity: 0.85;
  margin-top: 2px;
}

.mp-summary-bar {
  display: flex;
  align-items: stretch;
  gap: 20px;
  padding: 14px 24px;
  background: #f7f9fc;
  border-bottom: 1px solid #e6eaf0;
  flex-wrap: wrap;
}

.mp-summary-item {
  display: flex;
  flex-direction: column;
  min-width: 100px;
}

.mp-summary-label {
  font-size: 11px;
  color: #667;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.mp-summary-value {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin-top: 2px;
}

.mp-amount-chip {
  margin-left: auto;
  background: #e8f1ff;
  border: 1px solid #cfe0ff;
  border-radius: 8px;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  text-align: right;
}

.mp-amount-value {
  font-size: 18px;
  font-weight: 700;
  color: #0d47a1;
}

.mp-stepper {
  display: flex;
  align-items: flex-start;
  padding: 18px 32px;
  border-bottom: 1px solid #e6eaf0;
  overflow-x: auto;
}

.mp-step-wrap {
  display: flex;
  align-items: flex-start;
  flex: 1 1 auto;
}

.mp-step-wrap:last-child {
  flex: 0 0 auto;
}

.mp-step {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-shrink: 0;
}

.mp-step--clickable {
  cursor: pointer;
}

.mp-step--clickable .mp-step-title,
.mp-step--clickable .mp-step-circle {
  transition: opacity 0.15s ease;
}

.mp-step--clickable:hover .mp-step-title,
.mp-step--clickable:hover .mp-step-circle {
  opacity: 0.75;
}

.mp-step-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
}

.mp-step-circle--done {
  background: #0d47a1;
  color: #fff;
}

.mp-step-circle--current {
  background: #0d47a1;
  color: #fff;
  box-shadow: 0 0 0 3px rgba(13, 71, 161, 0.22);
}

.mp-step-circle--upcoming {
  background: #e3e8f0;
  color: #8a94a6;
}

.mp-step-text {
  min-width: 90px;
}

.mp-step-title {
  font-size: 13px;
  font-weight: 600;
  color: #8a94a6;
  white-space: nowrap;
}

.mp-step-title--done,
.mp-step-title--current {
  color: #0d47a1;
}

.mp-step-desc {
  font-size: 11px;
  color: #9aa3b2;
  white-space: nowrap;
}

.mp-step-line {
  flex: 1;
  height: 2px;
  background: #e3e8f0;
  margin: 15px 12px 0;
  min-width: 24px;
}

.mp-step-line--done {
  background: #0d47a1;
}

.mp-body-scroll {
  max-height: 62vh;
  overflow-y: auto;
}

.mp-body {
  display: flex;
  gap: 20px;
  padding: 20px 24px;
}

.mp-main {
  flex: 1 1 auto;
  min-width: 0;
}

.mp-sidebar {
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mp-card {
  border: 1px solid #e6eaf0;
  border-radius: 10px;
  background: #fff;
  margin-bottom: 16px;
}

.mp-card-header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 16px 18px;
  border-bottom: 1px solid #eef1f6;
}

.mp-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #0d1b2a;
}

.mp-card-subtitle {
  font-size: 12px;
  color: #78829a;
  margin-top: 2px;
}

.mp-card-body {
  padding: 18px;
}

.invoice-letterhead {
  background: #fff;
  padding: 22px 24px;
}

.invoice-letterhead__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 2px solid #184980;
  margin-bottom: 18px;
}

.invoice-letterhead__logo {
  height: 40px;
  width: auto;
  max-width: 280px;
  object-fit: contain;
  display: block;
}

.invoice-letterhead__badge {
  background: #184980;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 10px 18px;
  border-radius: 8px;
  text-align: center;
  white-space: nowrap;
}

.invoice-letterhead__meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
  background: #e0f0ff;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 20px;
}

.invoice-section {
  margin-bottom: 18px;
}

.invoice-section__title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #184980;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eef1f6;
}

.invoice-section--highlight {
  background: #e0f0ff;
  border-radius: 8px;
  padding: 14px 16px;
}

.invoice-letterhead__footer {
  margin-top: 22px;
  padding-top: 14px;
  border-top: 1px solid #eef1f6;
  font-size: 11px;
  color: #78829a;
}

.mp-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
}

.mp-field {
  display: flex;
  flex-direction: column;
}

.mp-field.full {
  grid-column: 1 / -1;
}

.label {
  font-size: 11px;
  color: #666;
  margin-bottom: 2px;
}

.value {
  font-size: 14px;
  font-weight: 600;
}

.amount-emphasis {
  color: #0d47a1;
  font-size: 16px;
}

.mp-accept-box {
  display: flex;
  align-items: flex-start;
  background: #eaf7ee;
  border: 1px solid #cdebd4;
  border-radius: 8px;
  padding: 14px;
  font-size: 13px;
  color: #1a1a1a;
}

.mp-info-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f5f7fa;
  border: 1px solid #e6eaf0;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  color: #556;
}

.mp-info-box--success {
  background: #eaf7ee;
  border-color: #cdebd4;
}

.mp-note {
  display: flex;
  align-items: flex-start;
  background: #fff8e1;
  border: 1px solid #ffe4a3;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 12px;
  color: #7a5b00;
  margin-bottom: 12px;
}

.mp-actions {
  display: flex;
  align-items: center;
  padding: 4px 0 16px;
}

.mp-complete-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 24px;
}

.mp-complete-title {
  font-size: 18px;
  font-weight: 700;
  margin-top: 12px;
}

.mp-complete-desc {
  font-size: 13px;
  color: #667;
  margin-top: 6px;
  max-width: 420px;
}

.mp-reject-reason {
  margin-top: 10px;
  background: #fdeded;
  border: 1px solid #f5c6c6;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #c62828;
}

.mp-reject-reason-title {
  font-weight: 700;
  margin-bottom: 4px;
}

.mp-side-card {
  border: 1px solid #e6eaf0;
  border-radius: 10px;
  background: #fff;
  padding: 16px;
}

.mp-side-card--status {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mp-side-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.mp-side-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #0d1b2a;
}

.mp-side-hint {
  font-size: 12px;
  color: #78829a;
  margin: 0 0 10px;
}

.mp-status-chip {
  display: inline-block;
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 700;
}

.mp-status-chip--pending,
.mp-status-chip--invoice_generated {
  background: #fff3e0;
  color: #e65100;
}

.mp-status-chip--reported {
  background: #e3f2fd;
  color: #1565c0;
}

.mp-status-chip--verified {
  background: #e8f5e9;
  color: #2e7d32;
}

.mp-status-chip--rejected {
  background: #ffebee;
  color: #c62828;
}

.mp-instruction {
  display: flex;
  gap: 10px;
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px dashed #e6eaf0;
}

.mp-instruction--last {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.mp-instruction-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e8f1ff;
  color: #0d47a1;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.mp-instruction-title {
  font-size: 13px;
  font-weight: 600;
}

.mp-instruction-desc {
  font-size: 12px;
  color: #78829a;
  margin-top: 2px;
}

.receipt-dropzone {
  position: relative;
  width: 100%;
  min-height: 168px;
  border: 2px dashed #b0bec5;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
}

.receipt-dropzone:hover:not(.receipt-dropzone--disabled) {
  border-color: #0d47a1;
  background: #f3f8ff;
}

.receipt-dropzone--active {
  border-color: #0d47a1;
  background: #e8f1ff;
  box-shadow: inset 0 0 0 1px rgba(13, 71, 161, 0.15);
}

.receipt-dropzone--disabled {
  opacity: 0.6;
  pointer-events: none;
  cursor: not-allowed;
}

.receipt-dropzone__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 28px 20px;
  pointer-events: none;
}

.receipt-dropzone__title {
  margin-top: 10px;
  font-size: 15px;
  font-weight: 600;
}

.receipt-dropzone__subtitle {
  margin-top: 4px;
  color: #757575;
  font-size: 14px;
}

.receipt-dropzone__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.receipt-file-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #d6e0ef;
  border-radius: 8px;
  background: #fff;
}

.receipt-file-card__icon {
  flex-shrink: 0;
}

.receipt-file-card__meta {
  flex: 1;
  min-width: 0;
}

.receipt-file-card__name {
  font-size: 14px;
  font-weight: 600;
  word-break: break-word;
}

.receipt-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 8px;
}

.receipt-preview-title {
  font-size: 12px;
  font-weight: 600;
  color: #1565c0;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.receipt-preview-frame {
  background: #fff;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  min-height: 280px;
  max-height: 420px;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.receipt-image {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
}

.receipt-iframe {
  width: 100%;
  height: 400px;
  border: 0;
  background: #fff;
}

.receipt-loading,
.receipt-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 16px;
  text-align: center;
  color: #607d8b;
  font-size: 13px;
}

.receipt-empty p {
  margin: 0;
}

.bank-box {
  background: #f5f8fc;
  border: 1px solid #d6e0ef;
  border-radius: 8px;
  padding: 14px 16px;
}

.bank-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 8px 0;
}

.bank-row + .bank-row {
  border-top: 1px solid #e3ebf5;
}

.bank-row .label {
  flex: 0 0 38%;
  font-size: 12px;
  color: #6b778c;
  padding-top: 2px;
}

.bank-row .value {
  font-size: 13px;
  font-weight: 600;
  color: #0d1b2a;
  text-align: right;
}

.bank-row__value {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 2px;
  min-width: 0;
}

.pay-method-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.pay-method-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  text-align: left;
  border: 1.5px solid #e3e8f0;
  border-radius: 10px;
  background: #fff;
  padding: 16px 14px;
  cursor: pointer;
  font-family: inherit;
}

.pay-method-card--selected {
  border-color: #0d47a1;
  background: #f3f8ff;
  box-shadow: 0 0 0 1px rgba(13, 71, 161, 0.12);
}

.pay-method-card--disabled {
  cursor: not-allowed;
  background: #f7f8fa;
  opacity: 0.85;
  padding-top: 28px;
}

.pay-method-card__title {
  font-size: 14px;
  font-weight: 700;
  color: #0d1b2a;
}

.pay-method-card__desc {
  font-size: 12px;
  color: #78829a;
  margin-top: 4px;
  line-height: 1.4;
}

.pay-method-soon {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #fff3e0;
  color: #e65100;
  border: 1px solid #ffcc80;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 2px 8px;
  text-transform: uppercase;
}

.how-to-pay__label {
  font-size: 12px;
  font-weight: 700;
  color: #0d47a1;
  margin-bottom: 8px;
}

.how-to-pay__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.how-to-pay__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #eef7f0;
  color: #2e7d32;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
}

.mp-swift-note {
  display: flex;
  align-items: flex-start;
  background: #eef5ff;
  border: 1px solid #d6e4f7;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  color: #1a3a63;
  line-height: 1.45;
}

@media (max-width: 900px) {
  .mp-body {
    flex-direction: column;
  }

  .mp-sidebar {
    flex: 1 1 auto;
  }

  .mp-summary-bar {
    flex-direction: column;
  }

  .mp-amount-chip {
    margin-left: 0;
    text-align: left;
  }

  .mp-grid-2 {
    grid-template-columns: 1fr;
  }

  .receipt-iframe {
    height: 280px;
  }

  .pay-method-grid {
    grid-template-columns: 1fr;
  }

  .bank-row {
    flex-direction: column;
    gap: 4px;
  }

  .bank-row .value,
  .bank-row__value {
    text-align: left;
    justify-content: flex-start;
  }
}
</style>
